import { yupResolver } from '@hookform/resolvers/yup';
// Removed import from @shared/documents
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { useUnitats, UnitatDocument } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import { LoadingGif, LoadingMessage } from '../Shared/CommonStyles';
import {
	Button,
	ErrorMessage,
	FormContainer,
	FormGroup,
	Input,
	Label,
	Select,
} from '../Shared/FormStyles';

import * as S from './PostForm.styles';

// Define the types locally
interface PostElementDoc {
	type: string;
	content: string;
}

interface PostDocument {
	_id: string;
	title: string;
	elements: PostElementDoc[];
	data_publicacio: Date | string;
	data_actualitzacio: Date | string;
	unitat?: string;
	url: string;
	data_publicacio_dd_mm_yyyy?: string;
	data_actualitzacio_dd_mm_yyyy?: string;
	preview_image: number;
}

type PostElementSubdocument = Omit<PostElementDoc, 'content'> & {
	content: string | Blob | null;
	preview?: string;
};

interface PostFormData {
	title: string;
	unitat: string;
}

const postSchema = yup.object().shape({
	title: yup
		.string()
		.required('Aquest camp és obligatori')
		.max(160, 'El títol no pot tenir més de 160 caràcters'),
	unitat: yup.string().required('Has de seleccionar una unitat'),
});

const PostForm = ({ mode }: { mode: string }) => {
	// TODO adaptar useValidatedParams perquè funcioni amb params opcionals o condicionals
	const { id, unitat: paramUnitat } = useParams();
	const { user } = useAuth();
	const { unitats, isFetchingUnitats } = useUnitats();

	const [authError, setAuthError] = useState<string | null>(null);
	const [formError, setFormError] = useState<string | null>(null);
	const [paramsError, setParamsError] = useState<string | null>(null);
	const [postElements, setPostElements] = useState<PostElementSubdocument[]>(
		[]
	);

	// Full unitat targeting (free unitat select incl. agrupament):
	// Admin/Agrupament/Caps. Unitat-role users are scoped server-side.
	const canTargetAllUnitats =
		user &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps'));

	// 'agrupament' user is authenticated too
	const isAuthenticated =
		!!user &&
		(canTargetAllUnitats ||
			(!!paramUnitat && user.unitat === paramUnitat) ||
			// Allow creating/editing if user has Unitat role (validation happens on submit/backend)
			user.roles.includes('Unitat'));

	const isEditMode = mode === 'edit';

	const {
		data: post,
		isLoading: isFetchingPost,
		error: fetchPostError,
	} = useFetch<PostDocument>({
		queryKey: ['post', id],
		url: `/api/posts/${id}`,
	});

	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(postSchema),
		defaultValues: {
			unitat: paramUnitat || 'agrupament',
		},
	});

	// Initialize unitat when user or param changes
	useEffect(() => {
		if (!isEditMode && user) {
			if (paramUnitat) {
				setValue('unitat', paramUnitat);
			} else if (user.unitat) {
				setValue('unitat', user.unitat);
			} else {
				setValue('unitat', 'agrupament');
			}
		}
	}, [isEditMode, user, paramUnitat, setValue]);

	const putMutation = useSimpleMutation<FormData>({
		method: 'put',
		url: `/api/posts/${id}`,
		dependencies: [id],
		queriesToInvalidate: [
			['post', id],
			post && post.unitat
				? ['posts', post.unitat]
				: ['posts', 'agrupament'],
			// The standalone posts list (Posts.tsx) caches under the 'tots'
			// bucket — invalidate it too or new/edited posts stay invisible there.
			['posts', 'tots'],
		],
		navigateUrl: '/posts',
	});

	const postMutation = useSimpleMutation<FormData>({
		method: 'post',
		url: '/api/posts',
		dependencies: [],
		queriesToInvalidate: [
			['posts', paramUnitat ? paramUnitat : 'agrupament'],
			['posts', 'tots'],
		],
		navigateUrl: '/posts',
	});

	const uniqueKey = `postFormState_${id || 'new'}_${
		paramUnitat || post?.unitat || 'agrupament'
	}`;

	const validatePostParagraphs = (elements: PostElementSubdocument[]) => {
		for (const element of elements) {
			if (element.type === 'paragraph') {
				if (!element.content) {
					return 'Els paràgrafs no poden estar buits';
				}
				if (typeof element.content !== 'string') {
					return 'Els paràgrafs han de ser de tipus text';
				}
				if (element.content.length > 10000) {
					return 'Els paràgrafs no poden tenir més de 10000 caràcters';
				}
			}
		}
		return null;
	};

	const clearPersistedState = () => {
		localStorage.removeItem(uniqueKey);
	};

	const onSubmit = async (formData: PostFormData) => {
		const validationError = validatePostParagraphs(postElements);
		if (validationError) {
			setFormError(validationError);
			return;
		}

		const submissionData = new FormData();

		submissionData.append('unitat', formData.unitat);
		submissionData.append('title', formData.title);

		postElements.forEach((element, index) => {
			if (element.content) {
				submissionData.append(`elements[${index}][type]`, element.type);
				submissionData.append(
					`elements[${index}][content]`,
					element.content
				);
			}
		});

		if (isEditMode) {
			putMutation.mutate(submissionData);
		} else {
			postMutation.mutate(submissionData);
		}

		clearPersistedState();
	};

	const handleFileChange = (file: FileList, index: number) => {
		const newPostElements = [...postElements];
		const element = newPostElements[index];

		if (element.preview) {
			URL.revokeObjectURL(element.preview);
		}

		element.content = file[0];
		element.preview = URL.createObjectURL(file[0]);

		setPostElements(newPostElements);
	};

	const addParagraph = () => {
		setPostElements([...postElements, { type: 'paragraph', content: '' }]);
	};

	const addImage = () => {
		setPostElements([...postElements, { type: 'image', content: null }]);
	};

	const addVideo = () => {
		setPostElements([...postElements, { type: 'video', content: '' }]);
	};

	const addVideoSelf = () => {
		setPostElements([...postElements, { type: 'video_self', content: null }]);
	};

	const updatePostElementContent = (
		index: number,
		content: string | Blob
	) => {
		const newPostElements = [...postElements];
		newPostElements[index].content = content;
		setPostElements(newPostElements);
	};

	const deletePostElement = (index: number) => {
		const newPostElements = [...postElements];
		const element = newPostElements[index];

		if (element.type === 'image' && element.preview) {
			URL.revokeObjectURL(element.preview);
		}

		newPostElements.splice(index, 1);
		setPostElements(newPostElements);
	};

	const renderPostElement = (
		element: PostElementSubdocument,
		index: number
	) => {
		switch (element.type) {
		case 'paragraph':
			return (
				<S.StyledMDEditor
					key={index}
					value={
						typeof element.content === 'string'
							? element.content
							: ''
					}
					// onChange={(e) =>
					// 	updatePostElementContent(index, e.target.value)
					// }
					onChange={(value) =>
						updatePostElementContent(index, value ?? '')
					}
					preview="edit" // Add this prop to hide the preview panel
				/>
			);
		case 'image':
			return (
				<div key={index}>
					{element.preview && (
						<S.PreviewImage
							src={element.preview}
							alt="Preview"
						/>
					)}
					<Input
						type="file"
						accept="image/*"
						onChange={(e) => {
							if (e.target.files) {
								handleFileChange(e.target.files, index);
							}
						}}
					/>
				</div>
			);
		case 'video':
			return (
				<div key={index}>
					<S.VideoUrlInput
						type="text"
						placeholder="Enganxa la URL del vídeo de YouTube"
						value={
							typeof element.content === 'string'
								? element.content
								: ''
						}
						onChange={(e) =>
							updatePostElementContent(index, e.target.value)
						}
					/>
				</div>
			);
		case 'video_self':
			return (
				<div key={index}>
					{element.preview && (
						<S.VideoPreview
							src={element.preview}
							controls
							preload="metadata"
						/>
					)}
					<Input
						type="file"
						accept="video/*"
						onChange={(e) => {
							if (e.target.files) {
								handleFileChange(e.target.files, index);
							}
						}}
					/>
				</div>
			);
		default:
			return null;
		}
	};

	useEffect(() => {
		if (isEditMode && !id) {
			setParamsError('Id no vàlid');
		}
	}, [id, isEditMode]);

	useEffect(() => {
		if (!user) {
			setAuthError('No estàs autenticat');
		} else if (!isAuthenticated) {
			setAuthError('No tens permís');
		} else {
			setAuthError(null);
		}
	}, [isAuthenticated, setAuthError, user]);

	useEffect(() => {
		if (post && !localStorage.getItem(uniqueKey)) {
			reset({ title: post.title, unitat: post.unitat || 'agrupament' });
			setPostElements(
				post.elements.map((item) => {
					if (item.type === 'image' || item.type === 'video_self') {
						return {
							...item,
							preview: item.content,
						};
					}
					return item;
				})
			);
		}
	}, [reset, post, uniqueKey]);

	useEffect(() => {
		// Load persisted state when component mounts
		const savedState = localStorage.getItem(uniqueKey);
		if (savedState) {
			const { title, elements, unitat } = JSON.parse(savedState);
			reset({ title, unitat });
			setPostElements(elements);
		}
	}, [reset, uniqueKey]);

	useEffect(() => {
		// Persist state to localStorage when form state or elements change

		if (postElements.length > 0) {
			localStorage.setItem(
				uniqueKey,
				JSON.stringify({
					title: watch('title'),
					unitat: watch('unitat'),
					elements: postElements,
				})
			);
		}
	}, [watch, postElements, uniqueKey]);

	const loading =
		isFetchingPost ||
		isFetchingUnitats ||
		putMutation.isPending ||
		postMutation.isPending;

	const error =
		authError ||
		paramsError ||
		formError ||
		fetchPostError ||
		putMutation.error ||
		postMutation.error;

	return loading ? (
		<LoadingMessage>
			<LoadingGif size="large" />
		</LoadingMessage>
	) : error ? (
		<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
	) : (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			{/* Unitat Selector */}
			<FormGroup>
				<Label htmlFor="unitat">Unitat:</Label>
				{canTargetAllUnitats ? (
					<Select id="unitat" {...register('unitat')}>
						<option value="agrupament">Agrupament</option>
						{unitats?.map((unitat: UnitatDocument) => (
							<option
								key={unitat._id}
								value={unitat.abreviacio.toLowerCase()}
							>
								{unitat.nom}
							</option>
						))}
					</Select>
				) : (
					<Input
						id="unitat"
						value={
							user?.unitat
								? unitats?.find(
									(u) =>
										u.abreviacio.toLowerCase() ===
											user.unitat
								)?.nom || user.unitat
								: 'Agrupament'
						}
						disabled
					/>
				)}
				{!canTargetAllUnitats && (
					<input
						type="hidden"
						{...register('unitat')}
					/>
				)}
				{errors.unitat && (
					<ErrorMessage>
						{errors.unitat.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="title">Títol:</Label>
				<Input id="title" {...register('title')} />
				{errors.title && (
					<ErrorMessage>
						{errors.title.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			{postElements.map((element, index) => (
				<S.PostElementsContainer key={index}>
					<S.PostElementContainer>
						{renderPostElement(element, index)}
						<Button
							type="button"
							onClick={() => deletePostElement(index)}
							style={{
								alignSelf: 'flex-end',
								backgroundColor: '#dc3545',
							}} // Add specific style for delete
						>
							Eliminar Element
						</Button>
					</S.PostElementContainer>
				</S.PostElementsContainer>
			))}

			<div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
				<Button type="button" onClick={addParagraph}>
					Afegir Paràgraf
				</Button>
				<Button type="button" onClick={addImage}>
					Afegir Imatge
				</Button>
				<Button type="button" onClick={addVideo}>
					Afegir Vídeo (YouTube)
				</Button>
				<Button type="button" onClick={addVideoSelf}>
					Afegir Vídeo (propi)
				</Button>
			</div>

			<Button
				type="submit"
				disabled={loading}
				style={{ marginTop: '1.5rem' }}
			>
				{loading
					? 'Guardant...'
					: isEditMode
						? 'Desar Canvis'
						: 'Crear Post'}
			</Button>
		</FormContainer>
	);
};

export default PostForm;